import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const EASE = [0.16, 1, 0.3, 1];

const CHAPTERS = [
  {
    n: "01",
    title: "ALWAYS OPEN.",
    body: "Rigs don't shut down at 5 PM — and neither do we. Nights, weekends, holidays: a live crew answers the phone 24 hours a day, 7 days a week, 365 days a year. When your pipe is on the ground, the clock is the only thing that matters.",
  },
  {
    n: "02",
    title: "IN-HOUSE & MOBILE.",
    body: "Haul it to our yard on Superior Road, or we bring the lab to your location. Fully equipped mobile inspection units run EMI, blacklight, and tool inspection right at the rack — no lost days, no dead iron waiting on a third party.",
  },
  {
    n: "03",
    title: "ONE CALL. FULL SERVICE.",
    body: "Inspection, blacklight, cleaning & coating, water blasting, storage, and trucking under one roof. We inspect it, clean it, coat it, store it, and haul it — so you deal with one crew instead of five vendors.",
  },
];

function Chapter({ chapter, index }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const numY = useTransform(scrollYProgress, [0, 1], [60, -60]);

  return (
    <div
      ref={ref}
      className="relative grid gap-6 border-t border-white/10 py-16 sm:py-20 lg:grid-cols-12 lg:gap-12"
      data-testid={`manifesto-chapter-${chapter.n}`}
    >
      <motion.span
        style={{ y: numY }}
        aria-hidden="true"
        className="pointer-events-none select-none font-mono text-[26vw] font-semibold leading-none text-white/[0.05] sm:text-[18vw] lg:col-span-4 lg:text-[11vw]"
      >
        {chapter.n}
      </motion.span>
      <div className="lg:col-span-8 lg:pt-10">
        <motion.h3
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.05 * index }}
          className="font-display text-3xl font-semibold uppercase tracking-tight text-slate-50 sm:text-4xl lg:text-5xl"
        >
          {chapter.title}
        </motion.h3>
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.12 + 0.05 * index }}
          className="mt-6 max-w-2xl text-base leading-relaxed text-slate-400 sm:text-lg"
        >
          {chapter.body}
        </motion.p>
      </div>
    </div>
  );
}

export default function Manifesto() {
  return (
    <section id="standard" className="relative bg-machine py-24 sm:py-32" data-testid="manifesto-section">
      <div className="mx-auto w-full max-w-[1700px] px-4 sm:px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: EASE }}
          className="mb-16 flex items-center gap-4"
        >
          <span className="font-mono text-[11px] uppercase tracking-[0.35em] text-uv">The Standard</span>
          <span className="h-px flex-1 bg-white/10" aria-hidden="true" />
          <span className="font-mono text-[11px] uppercase tracking-[0.35em] text-slate-500">Est. Church Point, LA</span>
        </motion.div>

        {CHAPTERS.map((c, i) => (
          <Chapter key={c.n} chapter={c} index={i} />
        ))}
      </div>
    </section>
  );
}
